import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Stack,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import type { SelectChangeEvent } from '@mui/material'

export interface FilterOption {
  value: string
  label: string
}

export interface MultiSelectFilter {
  name: string
  label: string
  options: FilterOption[]
  value: string[]
}

interface TableColumnFiltersProps {
  filters?: MultiSelectFilter[]
  onChange?: (filterName: string, selectedValues: string[]) => void
  onClearAll?: () => void
  // Set by FilterBar when one of its sibling autocomplete filters (e.g. a
  // company typeahead) has an active value. Those filters live outside the
  // `filters` prop this component receives, so without this, "Clear All"
  // stays hidden when only an autocomplete filter is active.
  hasAdditionalActiveFilters?: boolean
  // FilterBar's phone sheet passes 'medium': a small field is 37px tall, under
  // the 44px touch-target floor.
  size?: 'small' | 'medium'
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  // Ensure the dropdown renders above other content.
  slotProps: { paper: { sx: { zIndex: 1300 } } },
}

/**
 * Reusable multi-select filter row — ported from CrowCase
 * (common/table/TableColumnFilters.jsx). Each filter is a checkbox dropdown
 * whose selected values render as removable chips inside the control.
 */
export default function TableColumnFilters({
  filters = [],
  onChange,
  onClearAll,
  hasAdditionalActiveFilters = false,
  size = 'small',
}: TableColumnFiltersProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleFilterChange = (filterName: string, event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    onChange?.(filterName, typeof value === 'string' ? value.split(',') : value)
  }

  const hasActiveFilters =
    hasAdditionalActiveFilters || filters.some((filter) => (filter.value?.length ?? 0) > 0)

  return (
    <Box>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        alignItems={isMobile ? 'stretch' : 'center'}
        sx={{ flexWrap: 'wrap' }}
      >
        {filters.map((filter) => (
          <FormControl
            key={filter.name}
            sx={{
              minWidth: isMobile ? '100%' : 180,
              maxWidth: isMobile ? '100%' : 220,
              flex: '1 1 auto',
            }}
            size={size}
          >
            <InputLabel id={`${filter.name}-label`}>{filter.label}</InputLabel>
            <Select
              labelId={`${filter.name}-label`}
              id={filter.name}
              multiple
              value={filter.value || []}
              onChange={(e) => handleFilterChange(filter.name, e)}
              input={<OutlinedInput label={filter.label} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => {
                    const option = filter.options.find((opt) => opt.value === value)
                    return (
                      <Chip
                        key={value}
                        label={option?.label || value}
                        size="small"
                        onDelete={(e) => {
                          e.stopPropagation()
                          onChange?.(
                            filter.name,
                            filter.value.filter((v) => v !== value)
                          )
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    )
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {filter.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={(filter.value?.indexOf(option.value) ?? -1) > -1} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        {hasActiveFilters && onClearAll && (
          <Button
            variant="contained"
            size="small"
            startIcon={<ClearIcon />}
            onClick={onClearAll}
            sx={{ minWidth: isMobile ? '100%' : 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Clear All
          </Button>
        )}
      </Stack>
    </Box>
  )
}
